import { useCapcoEnabled } from '@/app/useCapcoEnabled';
import type { LabelValue } from '@/utils/label';
import PortionMark from './PortionMark';
import { labelMarking, labelText, storedLabelMarking } from './labelMarking';
import './marking.css';

interface Layout {
  /**
   * For a parent that truncates its text on ONE line (`truncate`, an
   * ellipsis). The mark and the text are then laid out as a row in which the
   * text takes the ellipsis first; the mark shrinks only once the text is at
   * its floor. Not for
   * a multi-line clamp: there the plain form already keeps the mark, at the
   * start of the first line.
   */
  truncate?: boolean;
}

interface FromLabel extends Layout {
  /** A metadata label or description: `{ lang, value, capco }`, or a plain string. */
  label: LabelValue;
  /**
   * Shown when the label has no text (an id, "Untitled Model"). It is not a
   * marked text, so it is shown with no mark.
   */
  fallback?: string;
  /**
   * What a text with NO stored marking shows.
   *  - `'U'` (the default): it is assumed unclassified and shows `(U)`. For a
   *    model's, task's or resource's name or description.
   *  - `'nothing'`: no mark. For a functional's, jar's or folder's name,
   *    where only a marking that is actually stored is shown.
   */
  whenUnmarked?: 'U' | 'nothing';
  text?: undefined;
  assertedMarking?: undefined;
}

interface FromPair extends Layout {
  /**
   * A string that is NOT read from a label here.
   */
  text: string;
  /**
   * The marking the CALLER asserts `text` carries. Nothing checks the pair:
   * this form can put any marking beside any text, so use it only where the
   * text cannot come from a label at the point of display, and derive the
   * marking from the stored object with `labelMarking`, `storedLabelMarking`
   * or `documentNameMarking` -- never from a literal, a URL or user input.
   * The call sites are listed in this directory's README. Empty: no mark.
   */
  assertedMarking: string | null | undefined;
  label?: undefined;
  fallback?: undefined;
  whenUnmarked?: undefined;
}

export type MarkedTextProps = FromLabel | FromPair;

/**
 * A text with its own portion mark before it: `(C) New Model`.
 *
 * It replaces `{getLabel(x) || fallback}` at a converted place where the text
 * is DISPLAYED, so the text cannot be shown without its marking. With CAPCO
 * disabled, or for the fallback, it renders the bare text and nothing else:
 * no wrapper element, the same DOM as before.
 *
 * Not for text a machine reads, an attribute holds or a sentence is built
 * from (`document.title`, an `aria-label`, a `title`, a sort key, a filename,
 * a toast): those keep `getLabel`.
 */
export default function MarkedText(props: MarkedTextProps) {
  const enabled = useCapcoEnabled();

  let text: string;
  let marking: string;
  if (props.text !== undefined) {
    text = props.text;
    marking = props.assertedMarking || '';
  } else {
    text = labelText(props.label);
    if (!text) return <>{props.fallback ?? ''}</>;
    marking = props.whenUnmarked === 'nothing' ? storedLabelMarking(props.label) : labelMarking(props.label);
  }

  // Decided here as well as in PortionMark: the space after the mark must not
  // be left behind when there is no mark.
  if (!enabled || !marking.trim()) return <>{text}</>;

  if (props.truncate) {
    return (
      <span className="marked-text-row">
        {/* The space is not laid out (a flex row drops it); it separates the
            mark from the text in the accessible name and in copied text. */}
        <PortionMark marking={marking} spaceAfter />
        <span className="marked-text-body">{text}</span>
      </span>
    );
  }
  return (
    <>
      <PortionMark marking={marking} /> {text}
    </>
  );
}
